import React, { useState, useEffect } from 'react';
import { Button, IconButton, TextField, Collapse, Box, useMediaQuery, useTheme, SwipeableDrawer, Paper, Typography, Link } from "@mui/material";
import styles from "./LandingPage.module.css";
import SendIcon from "@mui/icons-material/Send"
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase-config';
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import TermsAndConditionsPopup from './TermsAndConditionsPopup';
import { doc, setDoc, serverTimestamp, getDoc, updateDoc } from "firebase/firestore";
import { db } from '../firebase-config';

export default function LandingPage({user, isDarkMode}) {
    const [firstInput, setInput] = useState("");
    const [text, setText] = useState("");
    const [btnDisabled, setBtnDisabled] = useState(true);
    const [showTerms, setShowTerms] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedEmotion, setSelectedEmotion] = useState("");
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const handleRedirects = () => {
            // Check for stored redirect
            if (user) {
                const redirectPath = sessionStorage.getItem('redirectAfterAuth');
                if (redirectPath) {
                    sessionStorage.removeItem('redirectAfterAuth');
                    navigate(redirectPath);
                }
            }
        };

        handleRedirects();
    }, [user, navigate]);

    useEffect(() => {
        if (firstInput.trim() && !showTerms) { 
            navigate({
                pathname: 'chat-page',
                search: createSearchParams({
                    first: firstInput
                }).toString()
            });
        }
    }, [firstInput, navigate, showTerms]);

    const handleInputChange = (event) => {
        setText(event.target.value);
        setBtnDisabled(!event.target.value);  
    };

    const googleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        provider.addScope('profile');
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            console.log('User signed in:', user);
            
            // Create or update user profile in Firestore
            const userRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userRef);
            
            if (!userDoc.exists()) {
                // Only set initial values for new users
                await setDoc(userRef, {
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    interactionCount: 0,
                    createdAt: serverTimestamp(),
                });
            } else {
                // For existing users, only update their basic profile info
                await updateDoc(userRef, {
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && text.trim()) {
            event.preventDefault();
            setShowTerms(true);
        }
    };

    const handleEmotionButtonClick = (emotion, event) => {
        event.preventDefault();
        
        // Store the selected emotion
        setSelectedEmotion(emotion);
        
        // Close the drawer if it's open (for mobile)
        if (drawerOpen) {
            setDrawerOpen(false);
        }
        
        // Show Terms and Conditions popup
        setShowTerms(true);
    };

    const handleSendButtonClick = () => {
        if (text.trim()) {
            setShowTerms(true);
        }
    };

    const handleAcceptTerms = () => {
        setShowTerms(false);
        
        if (selectedEmotion) {
            // Set flag for new emotion click
            localStorage.setItem('new_emotion_click', 'true');
            
            // Navigate to chat page with emotion parameter
            navigate({
                pathname: 'chat-page',
                search: createSearchParams({
                    emotion: `I am feeling ${selectedEmotion}`
                }).toString()
            });
            
            // Reset selected emotion
            setSelectedEmotion("");
        } else if (text.trim()) {
            setInput(text);
        }
        
        // Clear the flag when navigating away
        localStorage.removeItem('isEmotionButtonMessage');
    };

    const renderDesktopEmotionDrawer = () => (
        <>
            <div className={styles.middle}>
                {["depressed", "lonely", "sad", "anxious", "guilty", "angry"].map((emotion, index) => (
                    <Button 
                        key={index}
                        sx={{ 
                            width: 320, 
                            borderRadius: '20px', 
                            color: 'text.primary', 
                            borderColor: 'secondary.main',
                            '&:hover': {
                                backgroundColor: 'secondary.main',
                                borderColor: 'secondary.main',
                            }
                        }} 
                        variant="outlined" 
                        onClick={(event) => handleEmotionButtonClick(emotion, event)}
                    >
                        {emotion}
                    </Button>
                ))}
                <div 
                    className={styles.drawerArrow}
                    onMouseEnter={() => setDrawerOpen(true)}
                    onMouseLeave={() => setDrawerOpen(false)}
                >
                    <KeyboardArrowDownIcon 
                        sx={{ 
                            color: 'accent.main',
                            transform: drawerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease-in-out'
                        }} 
                    />
                </div>
            </div>
            <Collapse in={drawerOpen} timeout="auto">
                <div 
                    className={styles.drawerContent}
                    onMouseEnter={() => setDrawerOpen(true)}
                    onMouseLeave={() => setDrawerOpen(false)}
                >
                    <div className={styles.middle}>
                        {["confident", "proud", "ecstatic", "happy", "grateful", "peaceful"].map((emotion, index) => (
                            <Button 
                                key={index}
                                sx={{ 
                                    width: 320, 
                                    borderRadius: '20px', 
                                    color: 'text.primary', 
                                    borderColor: 'accent.main',
                                    '&:hover': {
                                        backgroundColor: 'accent.main',
                                        borderColor: 'accent.main',
                                        color: 'background.default'
                                    }
                                }} 
                                variant="outlined" 
                                onClick={(event) => handleEmotionButtonClick(emotion, event)}
                            >
                                {emotion}
                            </Button>
                        ))}
                    </div>
                </div>
            </Collapse>
        </>
    );

    const renderMobileEmotionDrawer = () => (
        <>
            <div className={styles.middle}>
                {["depressed", "lonely", "sad", "anxious", "guilty", "angry"].map((emotion, index) => (
                    <Button 
                        key={index}
                        sx={{ 
                            width: 320, 
                            borderRadius: '20px', 
                            color: 'text.primary', 
                            borderColor: 'secondary.main',
                            '&:hover': {
                                backgroundColor: 'secondary.main',
                                borderColor: 'secondary.main',
                            }
                        }} 
                        variant="outlined" 
                        onClick={(event) => handleEmotionButtonClick(emotion, event)}
                    >
                        {emotion}
                    </Button>
                ))}
                <Button
                    className={styles.drawerArrow}
                    onClick={() => setDrawerOpen(true)}
                    sx={{
                        minWidth: 'unset',
                        padding: '5px 0',
                        borderRadius: '0',
                        width: '100%',
                        justifyContent: 'center',
                        '&:hover': { backgroundColor: 'transparent' }
                    }}
                >
                    <KeyboardArrowDownIcon 
                        sx={{ 
                            color: 'accent.main',
                        }} 
                    />
                </Button>
            </div>
            
            <SwipeableDrawer
                anchor="bottom"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onOpen={() => setDrawerOpen(true)}
                disableSwipeToOpen={false}
                swipeAreaWidth={30}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                        backgroundColor: theme.palette.background.default,
                        maxHeight: '50%',
                        padding: '16px 0'
                    }
                }}
            >
                <Box sx={{ 
                    width: '40px', 
                    height: '4px', 
                    backgroundColor: 'rgba(0, 0, 0, 0.1)', 
                    borderRadius: '2px',
                    margin: '8px auto 16px'
                }} />
                <div className={styles.middle}>
                    {["confident", "proud", "ecstatic", "happy", "grateful", "peaceful"].map((emotion, index) => (
                        <Button 
                            key={index}
                            sx={{ 
                                width: 320, 
                                borderRadius: '20px', 
                                color: 'text.primary', 
                                borderColor: 'accent.main',
                                margin: '4px 0',
                                '&:hover': {
                                    backgroundColor: 'accent.main',
                                    borderColor: 'accent.main',
                                    color: 'background.default'
                                }
                            }} 
                            variant="outlined" 
                            onClick={(event) => handleEmotionButtonClick(emotion, event)}
                        >
                            {emotion}
                        </Button>
                    ))}
                </div>
            </SwipeableDrawer>
        </>
    );

    const renderAuthenticatedUI = () => (
        <>
            {isMobile ? renderMobileEmotionDrawer() : renderDesktopEmotionDrawer()}
            <div className={styles.bottom}>
                <Box className={styles.messagetextareaframe} sx={{
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(245, 209, 199, 0.35)',
                }}>
                    <TextField
                        className={styles.typeYourMessage}
                        placeholder="Say something..."
                        color="primary"
                        variant="standard"
                        multiline
                        rows={1}
                        maxRows={1}
                        fullWidth
                        InputProps={{ 
                            disableUnderline: true, 
                            endAdornment: (
                                <IconButton
                                    color='primary'
                                    disabled={btnDisabled}
                                    onClick={handleSendButtonClick}
                                    sx={{ 
                                        color: btnDisabled ? 'rgba(255, 255, 255, 0.3)' : 'accent.main',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 212, 133, 0.1)',
                                        },
                                    }}
                                >
                                    <SendIcon />
                                </IconButton>
                            ),
                        }}
                        InputLabelProps={{ shrink: false }}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        value={text}
                    />
                </Box>
            </div>
        </>
    );

    const renderUnauthenticatedUI = () => (
        <>
            <div className={`${styles.middle} ${styles.googleSignInButton}`}>
                <button 
                    onClick={googleSignIn} 
                    style={{
                        background: `url('/web_dark_rd_ctn@2x.png') no-repeat center center`,
                        backgroundSize: 'contain',
                        width: '283.5px', 
                        height: '80px', 
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    aria-label="Sign in with Google"
                >
                    {/* Set to empty if your image contains all necessary text, otherwise you can place text here */}
                </button>
            </div>
        </>
    );

    return (
        <div className={`${styles.landingpage} ${showTerms ? styles.blurred : ''}`}>
            <div className={styles.top}>
            </div>
            <div>
                <h1>
                    how are you feeling today?
                </h1>
            </div>
            {user ? renderAuthenticatedUI() : renderUnauthenticatedUI()}
            <TermsAndConditionsPopup open={showTerms} onAccept={handleAcceptTerms} />
        </div>
    );
}